/*
 * PageWright — Favorites (S6, Phase 2), vanilla JS, no framework.
 *
 * Replaces the retired pw-saved.js (localStorage-only, products-only, disabled since
 * 2026-07-08). Backend-synced against the real wishlist API (packages/api/src/wishlist.ts,
 * live since PR #133): a favorite is a SubjectRef — either
 *   { kind:'product', handle }
 *   { kind:'page', productHandle, pageNumber }
 * — so both products AND individual book pages can be favorited with one schema.
 *
 * Storage: localStorage under 'pw_favorites' is the ALWAYS-available guest/offline layer.
 * When a My Library token exists (sessionStorage 'pw_library_token', same key pw-library.js
 * already writes), adds/removes also sync to the backend, and a one-time merge-on-login
 * unions any local guest favorites into the signed-in customer's server-side wishlist —
 * mirrors pw-library.js's own token seam, no new auth design.
 *
 * Mount: hearts attach ONLY to elements explicitly marked [data-pw-fav] (data-pw-fav-kind
 * = "product" | "page", data-pw-fav-handle, data-pw-fav-page for page-kind) — never a
 * DOM-guessed card container. This is a deliberate regression guard: the retired pw-saved.js
 * heuristically walked up to the nearest card-like ancestor element (product-card/card/
 * grid-item/list-item), which was the root cause of two real card-placement bugs on record.
 */
(function () {
  'use strict';

  var KEY = 'pw_favorites';
  /* Same literal PWAuth.TOKEN_KEY holds -- referenced directly so there's one real source of
   * truth once PWAuth has loaded (see layout/theme.liquid load order), literal kept as the
   * pre-load/failed-load fallback. */
  var TOKEN_KEY = (window.PWAuth && window.PWAuth.TOKEN_KEY) || 'pw_library_token';
  var LAST_MERGED_TOKEN_KEY = 'pw_favorites_last_merged_token';

  // PageWright's production backend — same site-wide-asset convention as
  // pw-search-collection-analytics.js (that file's own header comment explains the hand-edit-
  // for-staging convention; no per-instance dataset backend URL exists for a script loaded
  // once from the header, unlike pw-library.js/pw-book-pages.js which are scoped to a custom
  // element with its own data-backend-url).
  var BACKEND_URL = (window.PW_BACKEND_URL || '')  // set once by layout/theme.liquid from the
  // per-theme `backend_url` setting. Blank DISABLES backend calls rather than falling back
  // to production — a staging theme silently writing to prod data is the bug this replaces.

  // --- store -----------------------------------------------------------------------------

  function read() {
    try {
      return JSON.parse(localStorage.getItem(KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function subjectKey(subject) {
    if (!subject) return '';
    if (subject.kind === 'page') return 'page:' + subject.productHandle + ':' + subject.pageNumber;
    return 'product:' + subject.handle;
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* storage full/blocked — in-memory state below still reflects the change for this pageview */
    }
    updateCounts();
    syncHearts();
  }

  /**
   * Persist a thumbnail resolved after the fact (2026-08-02).
   *
   * WHY THIS IS NEEDED. `postFavoriteAdd` sends only the SUBJECT to the server, and
   * `mergeIfNeeded()` reads favourites back with no thumbUrl — so any favourite that round-trips
   * the server (a second device, or a merge after signing in) returns blank no matter how it was
   * created. Re-favouriting appears to fix it locally and it breaks again elsewhere, which is why
   * this looked like stale data rather than a design gap.
   *
   * Keyed by subject, never by array position: the list is re-sorted and re-merged constantly.
   * A null/empty thumbnail is ignored rather than written, so a failed lookup can never overwrite
   * a good thumbnail with nothing.
   */
  function setThumb(subject, thumbUrl) {
    if (!thumbUrl) return;
    var key = subjectKey(subject);
    if (!key) return;
    var list = read();
    var changed = false;
    for (var i = 0; i < list.length; i++) {
      if (subjectKey(list[i].subject) === key && !list[i].thumbUrl) {
        list[i].thumbUrl = thumbUrl;
        changed = true;
      }
    }
    if (changed) write(list);
  }

  function getFavorites() {
    return read();
  }

  function isFavorite(subject) {
    var key = subjectKey(subject);
    return read().some(function (item) {
      return subjectKey(item.subject) === key;
    });
  }

  function addFavorite(subject, thumbUrl) {
    if (isFavorite(subject)) return;
    var list = read();
    list.unshift({ subject: subject, addedAt: new Date().toISOString(), thumbUrl: thumbUrl || null });
    write(list);
    var backendUrl = currentBackendUrl();
    postFavoriteAdd(backendUrl, currentToken(), subject);
    postAnalyticsEvent(backendUrl, favoriteEventPayload('favorite_add', subject));
  }

  function removeFavorite(subject) {
    var key = subjectKey(subject);
    write(
      read().filter(function (item) {
        return subjectKey(item.subject) !== key;
      }),
    );
    var backendUrl = currentBackendUrl();
    postFavoriteRemove(backendUrl, currentToken(), subject);
    postAnalyticsEvent(backendUrl, favoriteEventPayload('favorite_remove', subject));
  }

  function toggleFavorite(subject, thumbUrl) {
    if (isFavorite(subject)) removeFavorite(subject);
    else addFavorite(subject, thumbUrl);
  }

  // --- backend url + token (mirrors pw-library.js's token seam) --------------------------

  function currentBackendUrl() {
    return BACKEND_URL;
  }

  /*
   * 2026-08-13/14 login forensics, defect #2: this used to read sessionStorage only, so a
   * browser restart de-identified the customer on every page this script runs on (site-wide).
   * pw-library.js got the sessionStorage->localStorage fallback fix 2026-08-02; this was one of
   * the two copies that fix missed. Delegates to the shared window.PWAuth (assets/pw-auth.js,
   * loaded before this file — see layout/theme.liquid) with the old inline logic kept as a
   * fallback so a failed asset load still degrades to correct (not regressed) behavior.
   */
  function currentToken() {
    if (window.PWAuth) return window.PWAuth.getToken();
    try {
      return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || undefined;
    } catch (e) {
      return undefined;
    }
  }

  // --- network: wishlist sync (fire-and-forget — local store is always the source of truth
  // for rendering; a failed sync just means this device's guest/local state didn't reach the
  // server yet, never a broken UI) -------------------------------------------------------

  function postFavoriteAdd(backendUrl, token, subject) {
    if (!backendUrl || !token) return;
    fetch(backendUrl + '/api/wishlist-add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ subject: subject, addedAt: new Date().toISOString() }),
      keepalive: true,
    }).catch(function () {
      /* local store already updated; sync will catch up on next merge */
    });
  }

  function postFavoriteRemove(backendUrl, token, subject) {
    if (!backendUrl || !token) return;
    fetch(backendUrl + '/api/wishlist-remove', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ subject: subject }),
      keepalive: true,
    }).catch(function () {
      /* local store already updated */
    });
  }

  function mergeOnLogin(backendUrl, token) {
    if (!backendUrl || !token) return Promise.resolve();
    var lastMerged;
    try {
      lastMerged = localStorage.getItem(LAST_MERGED_TOKEN_KEY);
    } catch (e) {
      lastMerged = null;
    }
    if (lastMerged === token) return Promise.resolve();

    return fetch(backendUrl + '/api/wishlist-merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ items: read() }),
    })
      .then(function (resp) {
        if (!resp.ok) return null;
        return resp.json();
      })
      .then(function (data) {
        try {
          localStorage.setItem(LAST_MERGED_TOKEN_KEY, token);
        } catch (e) {
          /* ignore */
        }
        if (data && Array.isArray(data.items)) write(data.items);
      })
      .catch(function () {
        /* merge is best-effort; local favorites remain intact either way */
      });
  }

  // --- analytics (dev-rules §13) — same beacon pipeline pw-image-search.js already uses ---

  var PW_SESSION_KEY = 'pw_session_id';

  function pwSessionId() {
    try {
      var id = sessionStorage.getItem(PW_SESSION_KEY);
      if (!id) {
        id = 'pw_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
        sessionStorage.setItem(PW_SESSION_KEY, id);
      }
      return id;
    } catch (e) {
      return undefined;
    }
  }

  function pwGaClientId() {
    var m = /(?:^|;\s*)_ga=GA1\.\d\.(\d+\.\d+)/.exec(document.cookie || '');
    return m ? m[1] : undefined;
  }

  function favoriteEventPayload(eventName, subject) {
    if (subject.kind === 'page') {
      return { event: eventName, handle: subject.productHandle, pageNumber: subject.pageNumber };
    }
    return { event: eventName, handle: subject.handle };
  }

  function postAnalyticsEvent(backendUrl, payload) {
    if (!backendUrl) return;
    try {
      var body = JSON.stringify(
        Object.assign({ eventVersion: 1, sessionId: pwSessionId(), clientId: pwGaClientId() }, payload),
      );
      var url = backendUrl + '/api/collect';
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
      }
    } catch (e) {
      /* analytics beacon must never break favoriting */
    }
  }

  // --- deep links --------------------------------------------------------------------------

  function deepLink(subject) {
    if (subject.kind === 'page') return '/products/' + subject.productHandle + '?pwpage=' + subject.pageNumber;
    return '/products/' + subject.handle;
  }

  // --- header count badge -------------------------------------------------------------------

  function updateCounts() {
    var n = read().length;
    document.querySelectorAll('[data-pw-saved-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  // --- explicit [data-pw-fav] mount ----------------------------------------------------------

  function subjectFromMount(mount) {
    var kind = mount.getAttribute('data-pw-fav-kind');
    var handle = mount.getAttribute('data-pw-fav-handle');
    if (!kind || !handle) return null;
    if (kind === 'page') {
      var pageNumber = parseInt(mount.getAttribute('data-pw-fav-page') || '', 10);
      if (!pageNumber) return null;
      return { kind: 'page', productHandle: handle, pageNumber: pageNumber };
    }
    return { kind: 'product', handle: handle };
  }

  /* The thumbnail already rendered next to this heart (data-pw-fav-thumb, stamped by whichever
   * card builder mounted the heart) — captured at favorite-time so My Favorites can show a real
   * image without a second fetch. Favorites saved before this existed have no thumbUrl and fall
   * back to buildGridTile's existing text-placeholder path. */
  function thumbFromMount(mount) {
    return mount.getAttribute('data-pw-fav-thumb') || null;
  }

  function syncHeart(mount) {
    var subject = subjectFromMount(mount);
    if (!subject) return;
    var saved = isFavorite(subject);
    mount.classList.toggle('is-saved', saved);
    mount.setAttribute('aria-pressed', saved ? 'true' : 'false');
    var glyph = mount.querySelector('[data-pw-fav-glyph]') || mount;
    glyph.textContent = saved ? '♥' : '♡';
  }

  function syncHearts(scope) {
    (scope || document).querySelectorAll('[data-pw-fav]').forEach(syncHeart);
  }

  function attachDelegatedClick() {
    document.addEventListener('click', function (e) {
      var mount = e.target.closest('[data-pw-fav]');
      if (!mount) return;
      var subject = subjectFromMount(mount);
      if (!subject) return;
      e.preventDefault();
      e.stopPropagation();
      toggleFavorite(subject, thumbFromMount(mount));
    });
  }

  // --- Ecomus one-time migration (ported from pw-saved.js, same source/keys) --------------

  var ECOMUS_KEY = 'theme4:wishlist:id';
  var ECOMUS_MIGRATED_KEY = 'pw_ecomus_migrated';
  var STOREFRONT_TOKEN = '5480c0564abe56e69d21d1bd0b85949a';
  var STOREFRONT_API_VERSION = '2026-01';

  function migrateEcomusWishlist() {
    try {
      var raw = localStorage.getItem(ECOMUS_KEY);
      if (!raw || localStorage.getItem(ECOMUS_MIGRATED_KEY)) return;
      localStorage.setItem(ECOMUS_MIGRATED_KEY, '1');

      var ids = raw
        .split(',')
        .map(function (s) {
          return s.trim();
        })
        .filter(function (s) {
          return /^\d+$/.test(s);
        });
      if (!ids.length) return;
      var gids = ids.slice(0, 250).map(function (id) {
        return 'gid://shopify/Product/' + id;
      });

      var endpoint = 'https://' + window.Shopify.shop + '/api/' + STOREFRONT_API_VERSION + '/graphql.json';
      var query = 'query($ids:[ID!]!){ nodes(ids:$ids) { ... on Product { handle } } }';

      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
        },
        body: JSON.stringify({ query: query, variables: { ids: gids } }),
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (res) {
          var nodes = (res && res.data && res.data.nodes) || [];
          var resolved = nodes.filter(function (n) {
            return n && n.handle;
          });
          if (!resolved.length) return;
          var list = read();
          var existingKeys = new Set(
            list.map(function (i) {
              return subjectKey(i.subject);
            }),
          );
          resolved.forEach(function (n) {
            var subject = { kind: 'product', handle: n.handle };
            var key = subjectKey(subject);
            if (existingKeys.has(key)) return;
            existingKeys.add(key);
            list.push({ subject: subject, addedAt: new Date().toISOString() });
          });
          write(list);
        })
        .catch(function () {
          /* silent — never break the page for this */
        });
    } catch (e) {
      /* localStorage disabled, malformed data, etc. — silent */
    }
  }

  // --- init ------------------------------------------------------------------------------

  function init() {
    syncHearts(document);
    updateCounts();
    migrateEcomusWishlist();
    var token = currentToken();
    if (token) mergeOnLogin(currentBackendUrl(), token);
  }

  attachDelegatedClick();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) {
    syncHearts(e.target);
  });
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) {
      updateCounts();
      syncHearts(document);
    }
  });
  window.addEventListener('pageshow', function () {
    updateCounts();
    syncHearts(document);
  });

  // My Library's Favorites tab calls this explicitly before rendering (rather than relying on
  // this module's own page-load merge, which is fire-and-forget and may not have resolved yet)
  // so the tab always shows post-merge data. No-ops (resolves immediately) when no library
  // token is available — matches mergeOnLogin's own guest no-op behavior.
  function mergeIfNeeded() {
    var token = currentToken();
    if (!token) return Promise.resolve();
    return mergeOnLogin(currentBackendUrl(), token);
  }

  window.PWFavorites = {
    getFavorites: getFavorites,
    addFavorite: addFavorite,
    removeFavorite: removeFavorite,
    setThumb: setThumb,
    isFavorite: isFavorite,
    toggleFavorite: toggleFavorite,
    deepLink: deepLink,
    mergeIfNeeded: mergeIfNeeded,
  };
})();
